import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { endpoints } from '../constants/endpoints';
import { isSubstringOfElement, uniqueBy } from '../helpers/helpers';
import { IDrug } from '../interfaces/drug.interface';
import { GetCustomDrugs, SetDrugsLoading } from '../states/drugs/drugs.actions';
import { IDrugShort } from '../interfaces/IDrugShort.interface';
import { PharmacyState } from '../states/pharmacy/pharmacy.state';
import { DrugScheduleEnum } from '../interfaces/DrugScheduleEnum ';
import { DrugActivitiesToTrackEnum } from '../interfaces/DrugActivitiesToTrackEnum';
import { DrugsState } from '../states/drugs/drugs.state';
import { ICustomDrug } from '../interfaces/custom-drug-interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DrugsService {
  // Remember this gets cleared after page refresh
  private drugs: IDrugShort[];
  private isLoading: boolean;
  public filteredDrugs$: Subject<IDrug[]> = new Subject<IDrug[]>();
  constructor(private http: HttpClient, private store: Store) {
  }

  fetchDrugs(): Observable<void> {
    if (!this.drugs?.length && !this.isLoading) {
      this.isLoading = true;
      this.store.dispatch(new SetDrugsLoading(true));
  
      return this.http.get<IDrugShort[]>(`${endpoints.drug.getAll}?all=${this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)}`)
        .pipe(
         take(1),
          map((resp: any) => {
            const enableC3toC5Drugs = this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs);
            const enableC3toC5DrugsValue = this.store.selectSnapshot(PharmacyState.enableC3toC5DrugsValue);
            const activity = enableC3toC5DrugsValue?.DrugsOfInterestType;
  
            let filteredDrugs: IDrugShort[] = resp?.value || [];
            if (enableC3toC5Drugs && activity === DrugActivitiesToTrackEnum.CABoard) {
              filteredDrugs = filteredDrugs.filter(ndc => 
                ndc.schedule === DrugScheduleEnum.C2 || 
                enableC3toC5DrugsValue.DrugsOfInterestValue?.some(x => 
                  ndc.friendlyName.toLowerCase().includes(x.toLocaleLowerCase()))
              );
            } else if (enableC3toC5Drugs && activity === DrugActivitiesToTrackEnum.AllAndDrugsOfInterest) {
              //filter based on pharmacy interest
              filteredDrugs = filteredDrugs.filter(ndc => 
                ndc.schedule !== DrugScheduleEnum.MISC || 
                enableC3toC5DrugsValue.DrugsOfInterestValue?.some(x => 
                  ndc.friendlyName.toLowerCase().includes(x.toLocaleLowerCase()))
              );
            } else {
              filteredDrugs = filteredDrugs.filter(ndc => ndc.schedule === DrugScheduleEnum.C2);
            }
  
            this.drugs = filteredDrugs.map(x => ({
              ...x,
              packageNdcs: DrugsService.removeDashesFromNdc(x)
            }));
  
            this.isLoading = false;
            this.store.dispatch(new SetDrugsLoading(false));
          }),
          catchError((error: any) => {
            this.isLoading = false;
            this.store.dispatch(new SetDrugsLoading(false));
            throw error;
          })
        );
    }
  }

  getDrugListFromSearch(token: string, alsoSearchWithinCustomDrugs: boolean = false) {
    if (token?.match('^$') || !this.drugs?.length) {
      this.fetchDrugs()
      ?.pipe(untilDestroyed(this))
      ?.subscribe(() => this.getDrugListFromSearch(token, alsoSearchWithinCustomDrugs));
      return;
    }

    if (alsoSearchWithinCustomDrugs && 
        this.store.selectSnapshot(PharmacyState.compoundingEnabled) && 
        !this.store.selectSnapshot(DrugsState.customDrugs)
    ) {
      this.store.dispatch(new GetCustomDrugs())
      .pipe(take(1))
      .subscribe(() => this.getDrugListFromSearch(token, true));
    }

    if (!alsoSearchWithinCustomDrugs) {
      this.search(token, this.drugs);
    } else {
      this.search(token, this.drugs.concat(this.store.selectSnapshot(DrugsState.customDrugs) as any[]));
    }
  }

  getCustomDrugsListFromSearch(token: string): void {
    if (!this.store.selectSnapshot(DrugsState.customDrugs)) {
    this.store.dispatch(new GetCustomDrugs())
    .pipe(take(1))
    .subscribe(() => this.getDrugListFromSearch(token, true));
  }
    this.search(token, this.store.selectSnapshot(DrugsState.customDrugs));
  }

  private search(token: string, source: any[]): void {
    const searchStr = (<any>token?.toLocaleLowerCase())?.replaceAll('-', '');
    const filtered = source?.filter(x => x?.friendlyName.toLocaleLowerCase().includes(searchStr)
      || x?.packageNdcs?.findIndex(ndc => isSubstringOfElement(ndc, searchStr)) > -1);

    const searchResult = uniqueBy(filtered, x => x.packageNdc);

    this.filteredDrugs$.next(searchResult);
  }

  clearDrugs() {
    this.drugs = [];
  }

  getSchedulesList(): number[] {
    let schedules = [DrugScheduleEnum.C2];
    if (this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      schedules = [...schedules, ...[DrugScheduleEnum.C3, DrugScheduleEnum.C4, DrugScheduleEnum.C5, DrugScheduleEnum.MISC]]
    }
    if (this.store.selectSnapshot(PharmacyState.compoundingEnabled)) {
      schedules = [...schedules, DrugScheduleEnum.Compound]
    }
    return schedules;
  }

  getSchedulesBasedOnExistingInventories(inventories: IDrug[]): number[] {
    let schedulesPresentInActiveInventories: number[] = [];
    function addScheduleIfInInventories(schedule: DrugScheduleEnum) {
      if(inventories?.findIndex(x => x.schedule === schedule) > -1) {
        schedulesPresentInActiveInventories.push(schedule);
      }
    }

    for (const schedule in DrugScheduleEnum ) {
      addScheduleIfInInventories(DrugScheduleEnum[schedule] as any);
    }
    return schedulesPresentInActiveInventories;
  }

  getDrugByNdc(ndc: string | number): IDrugShort | ICustomDrug {
    if (!this.drugs) {
      this.fetchDrugs()
      .pipe(untilDestroyed(this))
      .subscribe(() => this.getDrugByNdc(ndc));
    }
    if (ndc) {
      if (typeof(ndc) === 'string') {
        ndc = ndc.replace(/-/g, '');
      }
      const findDrugByNdcPredicate = (x: IDrugShort | ICustomDrug) => x.packageNdcs.findIndex(n => isSubstringOfElement(n, ndc)) > -1;
      return this.drugs?.find(findDrugByNdcPredicate) || this.store.selectSnapshot(DrugsState.customDrugs)?.find(findDrugByNdcPredicate);
    }
   return null;
  }

  public static removeDashesFromNdc(drug: IDrugShort | ICustomDrug): any {
    return drug?.packageNdcs?.map(ndc => (ndc as any).replaceAll('\n', '').replaceAll('-', ''));
  }

}
